import * as React from 'react';

import { Form } from '@independent-software/typeui/controls/Form';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { DataTable } from '@independent-software/typeui/controls/DataTable';
import { Number } from '@independent-software/typeui/formatters/Number';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { Input } from '@independent-software/typeui/controls/Input';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';

import { Result, ResultFactory } from '../../resource/'; 
import { List, IListProps } from '../../services/';
import { IAuth } from '../../AuthContext';
import { IFilter } from '../../FilterContext';
import { IconBar, Export } from '../../modules';
import { Gate } from '../../controls/Gate';
import { FilterPanel } from './FilterPanel';

interface IListResultsProps extends IListProps {
  filter: IFilter;
  /**
   * If set, list is prefiltered by a sample and
   * sample column and filter are hidden.
   */
  sampleId?: number;  
  /**
   * If set, list is prefiltered by a parameter and
   * parameter column and filter are hidden.
   */
  parameterId?: number;
  /**
   * If set, list is prefiltered by a station and
   * station column and filter are hidden.
   */
  stationId?: number;
  /**
   * Event is fired when a result is clicked.
   */
  onClick?: (result:Result) => void;
}

class ListResults extends List<Result, IListResultsProps, any> {
  constructor(props: IAuth & IListResultsProps) {
    super(props, ResultFactory, 'value', 'asc');

    if(this.props.sampleId) {
      this.setFilter('sample', 'in', this.props.sampleId.toString(), true);
    }
    if(this.props.parameterId) {
      this.setFilter('parameter', 'in', this.props.parameterId.toString(), true);
    }
    if(this.props.stationId) {
      this.setFilter('station', 'in', this.props.stationId.toString(), true);
    }

    this.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
  }

  handleChangeFilter = (
    parameters: number[], stations: number[], campaigns: number[], dateMin: string, dateMax: string,
    valueMin: string, valueMax: string,
    depthMin: string, depthMax: string) => 
  {
    this.setFilter('parameter', 'in', parameters.length ? parameters.join(',') : null);
    this.setFilter('station', 'in', stations.length ? stations.join(',') : null);
    this.setFilter('campaign', 'in', campaigns.length ? campaigns.join(',') : null);
    this.setFilter('date', 'gte', dateMin);
    this.setFilter('date', 'lte', dateMax);
    this.setFilter('value', 'gte', valueMin);
    this.setFilter('value', 'lte', valueMax);
    this.setFilter('depth', 'gte', depthMin);
    this.setFilter('depth', 'lte', depthMax);
  }

  getFilterArray = (filter: string): number[] => {
    const valueStr: string = this.getFilter(filter, 'in');
    if(valueStr == null) return [];
    return valueStr.split(',').map(s => parseInt(s));
  }

  render() {
    const p = this.props;

    const filter = 
      <Panel.Footer>
        <Gate right='can_delete_results'>
          <Checkbox label="Include deleted items" name="trash" type="toggle" checked={this.getTrash()} onChange={this.handleTrash}/>
        </Gate>
      </Panel.Footer>      

    return (
      <>
        <IconBar>
          <Panel.Icon icon="tools" width={350}>
            {filter}
          </Panel.Icon>
        </IconBar> 
        <DataTable error={this.state.error} loading={this.state.loading} scrollTop={this.state.scrollTop} onScroll={this.handleScroll} data={this.state.items} onFetch={this.handleFetch} onClick={p.onClick} onOrder={this.handleOrder} order={this.getOrder()} dir={this.getDir()}>
          {!p.stationId && <DataTable.Column weight={1} label="Station" order="stations.name" dir="asc">{(item:Result) => item.sample.station.name}</DataTable.Column>}
          <DataTable.Column weight={3} label="Campaign">{(item:Result) => item.sample.campaign.name}</DataTable.Column>
          <DataTable.Column weight={3} label="System">{(item:Result) => item.system_name}</DataTable.Column>
          {!p.sampleId && <DataTable.Column weight={2} label="Date" order="samples.date|samples.time" dir="asc">{(item:Result) => item.sample.date ? <><Datum.ShortDate value={item.sample.date}/> <Datum.Time value={item.sample.time}/></> : '-'}</DataTable.Column>}
          {!p.sampleId && <DataTable.Column weight={1} align="right" label="Depth" order="samples.depth" dir="asc">{(item:Result) => item.sample.depth ? <><Number value={item.sample.depth}/> m</> : '-'}</DataTable.Column>}
          {!p.parameterId && <DataTable.Column weight={2} label="Parameter" order="parameters.name" dir="asc">{(item:Result) => item.parameter.name}</DataTable.Column>}
          <DataTable.Column align="right" weight={1} label="Value">{(item:Result) => <Number value={item.value} decimals={item.parameter.decimals}/>}</DataTable.Column>
          <DataTable.Column weight={1} label="Detection limit">{(item:Result) => item.detection_limit ? <Icon name="check"/> : <Icon name="times"/>}</DataTable.Column>
          <DataTable.Column weight={1} label="Unit">{(item:Result) => item.parameter.unit}</DataTable.Column>
        </DataTable>
        <FilterPanel 
          parameters={this.getFilterArray('parameter')} 
          stations={this.getFilterArray('station')} 
          campaigns={this.getFilterArray('campaign')} 
          dateMin={this.getFilter('date', 'gte')}
          dateMax={this.getFilter('date', 'lte')}
          valueMin={this.getFilter('value', 'gte')}
          valueMax={this.getFilter('value', 'lte')}
          depthMin={this.getFilter('depth', 'gte')}
          depthMax={this.getFilter('depth', 'lte')}
          onChange={this.handleChangeFilter}
          onExport={this.handleExport}
        />
        <Dialog.Xhr open={this.state.exportError != null} error={this.state.exportError} onClose={this.handleCloseDialog}/>
      </>
    );
  }
}

export { ListResults };
